/**
 * Hide and Seek Teams Cinematics: the countdowns the players' Cinematics
 * overlay plays — before a round's hiding begins, and the big final seconds of
 * hiding and of seeking — and a preview of any of its moments on one player's
 * screen.
 */

import { useEffect, useState } from 'react';
import {
  GameType,
  HNST_STAGE_PREVIEW_KINDS,
  HNST_FINAL_COUNTDOWN_MAX,
  HNST_FINAL_COUNTDOWN_MIN,
  HNST_PRE_ROUND_COUNTDOWN_MAX,
  HNST_PRE_ROUND_COUNTDOWN_MIN,
  getHnstCountdownConfig,
  type Game,
  type HnstActionResponse,
  type HnstStagePreviewKind,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from '../../contexts/GameContext';
import { useHnstContext } from '../../contexts/HnstContext';
import { PreviewTargetSelect } from '../PreviewTargetSelect';

export function HnstCinematicsTab() {
  const { selectedGame: game } = useGameContext();
  if (!game || game.gameType !== GameType.HIDE_AND_SEEK_TEAMS) return null;
  return <CinematicsTab game={game} />;
}

function CinematicsTab({ game }: { game: Game }) {
  const [error, setError] = useState<string | null>(null);

  /** Run an action and show its error, if any. */
  const run = async (action: Promise<HnstActionResponse>) => {
    const res = await action;
    setError(res.success ? null : res.error ?? 'Something went wrong');
  };

  return (
    <div className="d-flex flex-column gap-base text-white">
      <h2>Cinematics</h2>
      <p className="field__hint">
        The players' Cinematics overlay plays the big moments over the game: the coin toss, a countdown and round card
        as each round starts, the final seconds of hiding and of seeking, who gets caught, and the round and game results.
        Each player can turn it off or switch it to Lite in their overlay settings.
      </p>
      {error && <div className="alert alert--red">{error}</div>}
      <CountdownsPanel game={game} run={run} />
      {!game.archived && <PreviewPanel game={game} run={run} />}
    </div>
  );
}

const PREVIEW_LABELS: Record<HnstStagePreviewKind, string> = {
  coin: 'Coin toss',
  'pre-round': 'Round start, with countdown',
  'round-intro': 'Round start, no countdown',
  'final-hiding': 'Final seconds of hiding',
  'final-seeking': 'Final seconds of seeking',
  'round-over': 'Round over',
  'game-over': 'Game over, with confetti',
  awards: 'Awards',
  'results-link': 'Results link and QR code',
  'caught-self': 'Caught: the player',
  'caught-catcher': 'Caught: by the player',
  'caught-teammate': 'Caught: a teammate',
  'caught-other': 'Caught: someone else',
  'caught-burst': 'Caught: five at once',
  'caught-during-countdown': 'Caught during a countdown',
};

function PreviewPanel({ game, run }: { game: Game; run: (action: Promise<HnstActionResponse>) => Promise<void> }) {
  const actions = useHnstContext();
  const [target, setTarget] = useState('');
  const [kind, setKind] = useState<HnstStagePreviewKind>('pre-round');

  return (
    <div className="card card--sm card--bg-midnight card--border card--border-navy">
      <h3>Preview</h3>
      <p className="field__hint">
        Play a moment on one player's screen, with sample data from this game, to see how it looks over SWTOR. It
        only shows if they have the Cinematics overlay on; nothing changes in the game.
      </p>
      <PreviewTargetSelect players={game.players} value={target} onChange={setTarget} />
      <div className="d-flex flex-wrap gap-sm align-items-end">
        <div className="field">
          <label htmlFor="hnst-preview-kind">Moment</label>
          <select id="hnst-preview-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value as HnstStagePreviewKind)}>
            {HNST_STAGE_PREVIEW_KINDS.map((each) => (
              <option key={each} value={each}>
                {PREVIEW_LABELS[each]}
              </option>
            ))}
          </select>
        </div>
        <button className="btn btn--aqua" disabled={!target} onClick={() => run(actions.previewCinematic(target, kind))}>
          ▶️ Preview
        </button>
      </div>
    </div>
  );
}

function CountdownsPanel({ game, run }: { game: Game; run: (action: Promise<HnstActionResponse>) => Promise<void> }) {
  const actions = useHnstContext();
  const saved = getHnstCountdownConfig(game);
  const [preRoundSeconds, setPreRoundSeconds] = useState(saved.preRoundSeconds);
  const [finalSeconds, setFinalSeconds] = useState(saved.finalSeconds);
  const ended = game.status === 'completed' || game.status === 'abandoned' || !!game.archived;

  // Follow a change saved elsewhere (another admin tab).
  useEffect(() => {
    setPreRoundSeconds(saved.preRoundSeconds);
    setFinalSeconds(saved.finalSeconds);
  }, [saved.preRoundSeconds, saved.finalSeconds]);

  const unchanged = preRoundSeconds === saved.preRoundSeconds && finalSeconds === saved.finalSeconds;

  return (
    <div className="card card--sm card--bg-midnight card--border card--border-navy">
      <h3>Countdowns</h3>
      <p className="field__hint">
        A change applies from the next round to start. 0 turns a countdown off: with no pre-round countdown hiding begins
        the moment you start the round; with no final countdown the players still see "Hunt begins" and "Time's up".
      </p>
      <div className="d-flex flex-wrap gap-sm align-items-end">
        <div className="field">
          <label htmlFor="hnst-pre-round-seconds">Before hiding begins (seconds)</label>
          <input
            id="hnst-pre-round-seconds"
            className="input"
            type="number"
            min={HNST_PRE_ROUND_COUNTDOWN_MIN}
            max={HNST_PRE_ROUND_COUNTDOWN_MAX}
            value={preRoundSeconds}
            disabled={ended}
            onChange={(e) => setPreRoundSeconds(Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="hnst-final-seconds">Final seconds of hiding and seeking</label>
          <input
            id="hnst-final-seconds"
            className="input"
            type="number"
            min={HNST_FINAL_COUNTDOWN_MIN}
            max={HNST_FINAL_COUNTDOWN_MAX}
            value={finalSeconds}
            disabled={ended}
            onChange={(e) => setFinalSeconds(Number(e.target.value))}
          />
        </div>
        <button
          className="btn btn--outline btn--slate"
          disabled={ended || unchanged}
          onClick={() => run(actions.configureCountdowns({ preRoundSeconds, finalSeconds }))}
        >
          Save
        </button>
      </div>
    </div>
  );
}
